import React, {

    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { formatValueAsDateAndTime } from "../../../helpers/utils/DateUtils";
import DiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import Modal from "../../../components/common/form/Modal";
import { DataTableColumn } from "../../../components/common/datatable/DataTableTypes";
import { DataTable } from "../../../components/common/datatable/DataTable";
import { useLazyGetOrderResultHistoryByIdQuery, useSearchOrderResulHistoryByProductIdQuery } from "../productsApi";
import { createFormattedDate } from "../../../helpers/utils/Utils";

interface OrderResultListItem {
    id: string;
    creationDate: string;
    createdBy: string;
}

interface OrderResultFull extends OrderResultListItem {
    orderResult?: string;
}

interface ReadDiffViewerModalProps {
    show: boolean;
    setShow: (value: boolean) => void;
    productId: any;
}

export const ReadDiffViewerModal: React.FC<ReadDiffViewerModalProps> = ({ show, setShow, productId }) => {

    const orderResultData = useSearchOrderResulHistoryByProductIdQuery({ productId: productId });

    const [selectedNew, setSelectedNew] = useState<OrderResultListItem | null>(null);
    const [selectedOld, setSelectedOld] = useState<OrderResultListItem | null>(null);

    const [fullNew, setFullNew] = useState<OrderResultFull | null>(null);
    const [fullOld, setFullOld] = useState<OrderResultFull | null>(null);
    const [isLoadingDiff, setIsLoadingDiff] = useState<boolean>(false);

    const [triggerGetById] = useLazyGetOrderResultHistoryByIdQuery();

    const rowRefs = useRef<Record<string, HTMLTableRowElement>>({});

    useLayoutEffect(() => {
        if (!orderResultData.data) return;

        orderResultData.data.data.forEach((history: any) => {
            const tr = rowRefs.current[history.id];
            if (!tr) return;

            tr.style.removeProperty('background-color');
            tr.querySelectorAll('td').forEach((td) => (td as HTMLElement).style.removeProperty('background-color'));

            tr.classList.remove('!bg-diff-new/50', '!bg-diff-old/50');
            tr.querySelectorAll('td').forEach((td) => (td as HTMLElement).classList.remove('!bg-diff-new/50', '!bg-diff-old/50'));

            if (selectedNew?.id === history.id) {
                tr.classList.add('!bg-diff-new/50');
                tr.querySelectorAll('td').forEach((td) => (td as HTMLElement).classList.add('!bg-diff-new/50'));
            } else if (selectedOld?.id === history.id) {
                tr.classList.add('!bg-diff-old/50');
                tr.querySelectorAll('td').forEach((td) => (td as HTMLElement).classList.add('!bg-diff-old/50'));
            }
        });
    }, [selectedNew, selectedOld, orderResultData.data]);

    function selectOrderResults(props: any) {
        const formattedDate = formatValueAsDateAndTime(props.row.original.creationDate, 'MM/DD/YY h:mm:ss a');

        return (
            <span
                id={props.row.original.id}
                title={formattedDate as string}
                className="formatted_date cursor"
                ref={(el: HTMLSpanElement | null) => {
                    if (el) {
                        const tr = el.closest("tr") as HTMLTableRowElement;
                        tr.classList.add("cursor-pointer");
                        rowRefs.current[props.row.original.id] = tr;

                        tr.addEventListener("click", async (e: MouseEvent) => {
                            e.preventDefault();
                            const index = props.data.findIndex((cur: any) => cur.id === props.row.original.id);

                            const newSel = props.row.original as OrderResultListItem;
                            const oldSel = props.data.length > index + 1 ? (props.data[index + 1] as OrderResultListItem) : null;

                            // Start loading immediately and clear current diff content
                            setIsLoadingDiff(true);
                            setFullNew(null);
                            setFullOld(null);

                            // Update selection (titles will reflect immediately)
                            setSelectedNew(newSel);
                            setSelectedOld(oldSel);

                            try {
                                const [newResp, oldResp] = await Promise.all([
                                    triggerGetById(newSel.id).unwrap(),
                                    oldSel ? triggerGetById(oldSel.id).unwrap() : Promise.resolve(null),
                                ]);
                                setFullNew(newResp);
                                setFullOld(oldResp);
                            } catch (err) {
                                // swallow handled errors
                            } finally {
                                setIsLoadingDiff(false);
                            }
                        });
                    }
                }}
            >
                {formattedDate}
            </span>
        );
    }

    const columns: DataTableColumn[] = [
        {
            label: "Time",
            accessor: "creationDate",
            canSort: false,
            cell: (props: any) => selectOrderResults(props),
        },
        {
            label: "Editor",
            accessor: "createdBy",
            canSort: false,
        }
    ]

    const customFilter = useMemo(() => ({ productId }), [productId]);

    function getFormattedValue(orderResults: OrderResultFull | null): string {
        if (!orderResults?.orderResult) return "";
        const cleanedString = orderResults.orderResult.trim();
        try {
            const parsed = JSON.parse(cleanedString);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return cleanedString;
        }
    }

    const leftTitle = isLoadingDiff
        ? "Loading..."
        : (selectedNew !== null
            ? createFormattedDate(selectedNew?.creationDate, 'MM/DD/YY h:mm:ss a')
            : "No Result Selected");

    const rightTitle = isLoadingDiff
        ? "Loading..."
        : (selectedOld !== null ? createFormattedDate(selectedOld?.creationDate, 'MM/DD/YY h:mm:ss a') : "No Result Selected");

    return (
        <Modal
            title={"Timeline"}
            show={show}
            setShow={setShow}
            className={"w-3/4"}
            fields={
                <>
                    <div className="modal-body">
                        <p className="ml-1">Select the Results you would like to compare</p>
                        <div className="d-flex flex-row gap-3">
                            {/* Left Column - Table */}
                            <div className="flex-shrink-0" style={{ flexBasis: "35%" }}>
                                <div className="table_data h-100">
                                    <div className="table-responsive jurisdictions_selector">
                                        <DataTable
                                            columns={columns}
                                            actions={{
                                                fetchAction: useSearchOrderResulHistoryByProductIdQuery,
                                            }}
                                            appendFilterToUrl={false}
                                            defaultFilter={customFilter}
                                            basePermission={"ORDR"}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Diff Viewer */}
                            <div className="flex-grow-1 w-full">
                                <div style={{ height: '60vh', maxHeight: '60vh', overflowY: 'auto' }} className="relative">
                                    <DiffViewer
                                        useDarkTheme={true}
                                        oldValue={getFormattedValue(fullOld)}
                                        newValue={getFormattedValue(fullNew)}
                                        compareMethod={DiffMethod.WORDS}
                                        splitView={false}
                                        leftTitle={leftTitle}
                                        rightTitle={rightTitle}
                                        renderContent={(source: string) => (
                                            <pre style={{ color: "white", whiteSpace: "pre-wrap", margin: 0 }}>{source}</pre>
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }
        />
    );
};